import { createFileRoute, Outlet, Link, useLocation } from '@tanstack/react-router'
import { supabase } from '@/integrations/supabase/client'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, X } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTracking } from '@/hooks/use-tracking'
import { MainLayout } from '@/components/layout/main-layout'


export const Route = createFileRoute('/guias')({
  component: GuiasLayout,
})

function GuiasLayout() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const location = useLocation()
  const { registrar } = useTracking()

  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data: categorias } = useQuery({
    queryKey: ['categorias-blog'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categorias_blog')
        .select('*')
        .order('ordem', { ascending: true })
      if (error) throw error
      return data
    },
  })

  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['blog-search', debouncedSearch],
    queryFn: async () => {
      if (!debouncedSearch || debouncedSearch.length < 2) return []
      const { data, error } = await supabase
        .from('artigos')
        .select('titulo, slug, categorias_blog(slug)')
        .eq('status', 'publicado')
        .ilike('titulo', `%${debouncedSearch}%`)
        .limit(5)
      if (error) throw error
      return data
    },
    enabled: debouncedSearch.length >= 2
  })


  return (
    <MainLayout>
      <div className="bg-[#F7F9F5] min-h-screen pb-20">
        <Helmet>
          <title>Guias e Dicas para Lojistas | Gopage</title>
          <meta name="description" content="Aprenda a encontrar os melhores fornecedores, conheça os polos atacadistas e otimize a operação da sua loja com nossos guias completos." />
        </Helmet>

        <div className="bg-[#0C1A12] py-16 mb-12 relative overflow-hidden">
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Guias para lojistas
            </h1>
            <p className="text-[#E5E7EB] text-lg max-w-2xl mx-auto mb-8">
              Tudo o que você precisa saber sobre fornecedores, polos atacadistas e operação de loja.
            </p>

            <div className="max-w-xl mx-auto relative">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280] group-focus-within:text-[#C6F764] transition-colors" />
                <Input 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar guias e artigos..."
                  className="pl-12 pr-12 h-14 bg-white/10 border-white/20 text-white placeholder:text-[#6B7280] rounded-2xl focus:bg-white focus:text-[#0C1A12] focus:placeholder:text-[#6B7280] transition-all"
                />
                {search && (
                  <button 
                    onClick={() => setSearch('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0C1A12]"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {search.length >= 2 && !isSearching && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-[#E5E7EB] overflow-hidden z-50 text-left">
                  {searchResults && searchResults.length > 0 ? (
                    searchResults.map((result: any) => (
                      <Link
                        key={result.slug}
                        to="/guias/$categoria/$slug"
                        params={{ 
                          categoria: result.categorias_blog?.slug || 'geral',
                          slug: result.slug
                        }}
                        onClick={() => {
                          setSearch('')
                          registrar('busca' as any, { termo: `autocomplete:${search} -> ${result.titulo}`, resultados: searchResults.length })
                        }}
                        className="block px-6 py-4 hover:bg-[#F7F9F5] text-[#1A1A1A] font-medium border-b border-[#E5E7EB] last:border-0 transition-colors"
                      >
                        {result.titulo}
                      </Link>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <p className="text-[#6B7280] mb-4">Nenhum resultado encontrado para "{search}"</p>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            <Link 
              to="/guias"
              className={`px-6 py-2 rounded-full font-bold transition-all ${
                location.pathname === '/guias' 
                  ? 'bg-[#C6F764] text-[#0C1A12]' 
                  : 'bg-white text-[#6B7280] hover:bg-[#E5E7EB]'
              }`}
            >
              Todos
            </Link>
            {categorias?.map((cat: any) => (
              <Link 
                key={cat.id}
                to="/guias/$categoria"
                params={{ categoria: cat.slug }}
                className={`px-6 py-2 rounded-full font-bold transition-all ${
                  location.pathname.includes(`/guias/${cat.slug}`)
                    ? 'bg-[#C6F764] text-[#0C1A12]' 
                    : 'bg-white text-[#6B7280] hover:bg-[#E5E7EB]'
                }`}
              >
                {cat.nome}
              </Link>
            ))}
          </div>

          <Outlet />
        </div>
      </div>
    </MainLayout>
  )
}